import type { World } from '../World';
import type { RunState } from '../run/RunState';
import type { Player } from '../entities/Player';
import { isSpecialEnemy } from '../entities/Enemy';
import { skillParams } from '../abilities/AbilityDefs';
// NOTE: skillHooks imports aspectDamageMult from this file — a module cycle,
// but runtime-only on both sides (all uses are inside function bodies), so
// the ESM live bindings resolve fine. Don't add top-level evaluation.
import { skillOutgoingDamageMult } from '../abilities/skillHooks';
import { gameplayRng } from '../../core/rng/RngStreams';
import { dist2 } from '../../core/math';
import { SIM_HZ } from '../../core/GameLoop';

/**
 * Aspects: the per-character rule that changes what your hands do. All sim-
 * side, all live-position/gameplayRng only — the mite/runner determinism
 * class applies to every entity here (crawlers, the hound): they must NEVER
 * touch a seeded stream or mint a pattern runner.
 *
 * Placement rule: run-persistent scalars live on RunState (candle, revive,
 * corpse reserve); per-floor transients live on World (crawlers, hound,
 * ironbound stacks, sunder timers) and re-materialize on floor load.
 */

export type AspectId =
  | 'guttering' | 'ossuary' | 'ninefold' | 'ironbound' | 'sunder'
  | 'sharedFate' | 'shortcut' | 'ebb';

// ---- Tuning constants (all provisional) ----
// Full → empty over a floor's dwell. 98 = old 75 × the measured +30% floor
// area of chunk maps (MAPGEN_PLAN §6/P2); flat bump for now — revisit if
// archetype size variance gets large (tuning).
export const CANDLE_SECONDS = 98;
export const GUTTERING_MAX_BONUS = 0.6; // +60% damage at a guttered candle
export const GUTTERING_CHIP_PERIOD = 4; // s between self-chips at empty
/** Flare (MA rework 2026-07-28): 10s of candle buys this much extra damage
 *  for this long — a purchased spike riding the same Guttering fold. */
export const FLARE_BUFF_SECONDS = 5;
export const FLARE_BUFF_BONUS = 0.5;
/** (tuning) Candle seconds restored per floor-content interaction (chest,
 *  shrine, vault altar) — chunk maps put rewards in dead-end pockets, so
 *  detours pay their own candle toll at the moment the reward lands. */
export const RELIGHT_EVENT_SECONDS = 15;
/** Crawler cap grows with depth (Sarah, 2026-07-28): the swarm scales where
 *  the ×2-density floors need it. 12 / 14 / 16 / 18 / 20 on floors 1-5+. */
export const CRAWLER_CAP_BASE = 12;
export const CRAWLER_CAP_MAX = 20;
export function crawlerCap(floor: number): number {
  return Math.min(CRAWLER_CAP_MAX, CRAWLER_CAP_BASE + 2 * (floor - 1));
}
export const CORPSE_RESERVE_CAP = 30;
export const CRAWLER_SPEED = 240;
export const CRAWLER_BITE_PERIOD = 0.5;
/** Exhume enrage (2026-07-28): the press whips the swarm — faster, harder,
 *  and it goes for SPECIALS first while the fury lasts. */
export const CRAWLER_ENRAGE_SECONDS = 6;
export const CRAWLER_ENRAGE_SPEED = 1.5;
export const CRAWLER_ENRAGE_DAMAGE = 1.5;
/** Ambient birth: below this many live crawlers, the rot births its own —
 *  free (no kill, no reserve). The bootstrap for a kill-fed engine. */
export const AMBIENT_CRAWLER_FLOOR = 3;
export const AMBIENT_BIRTH_PERIOD = 3.5; // s
/** Boss duels starve the corpse economy (no adds) — the reserve becomes the
 *  fuel: while the boss is active, births above the free ambient floor SPEND
 *  reserve at a faster clip to hold a fighting swarm. PET_BOSS_FACTOR keeps
 *  the dps honest; the reserve surviving descent is what makes this a
 *  strategic stockpile rather than a freebie. */
export const BOSS_SWARM_FLOOR = 6;
export const BOSS_BIRTH_PERIOD = 1.5; // s
/** Crawlers only hunt prey this close to MOURNE (not to themselves) — the
 *  leash that stops the swarm chain-straying across the map. */
export const CRAWLER_HUNT_RADIUS = 450;
/** Pets (crawlers, the hound) engage boss-class enemies at this damage
 *  factor — full-rate untargetable pets would delete the Keeper in ~2s. */
export const PET_BOSS_FACTOR = 0.3;
export const NINEFOLD_IFRAME_SECONDS = 1.5;
export const IRONBOUND_STACK_SECONDS = 6; // taken damage → aura, then fades
export const IRONBOUND_AURA_RADIUS = 110;
export const IRONBOUND_DPS_PER_STACK = 4;
export const SUNDER_PERIOD = 12; // s between burst windows
export const SUNDER_WINDOW = 4; // s of split form
export const SUNDER_HASTE = 2.5; // skills fire this much faster while split (3 → 2.5, round-5 trim)
/** Sunder nova bookends (2026-07-28, Sarah's elemental ask): splitting
 *  releases a CHILL nova, re-merging detonates a BURN nova + scorched
 *  ground. Radius/damage are Flare's old nova class. */
export const SUNDER_NOVA_RADIUS = 130;
export const SUNDER_NOVA_DAMAGE = 25;
export const SUNDER_CHILL_SECONDS = 2.5; // matches Rime's pulse
export const SUNDER_BURN_RADIUS = 90;
export const SUNDER_BURN_DPS = 12;
export const SUNDER_BURN_SECONDS = 2.5;
export const HOUND_HP = 24;
export const HOUND_VACUUM_RANGE = 420;
export const HOUND_DASH_SPEED = 700;
/** Retune 2026-07-27 (probe: dash-only = 4-16 k/min vs ~74 roster
 *  baseline): Gaunt BITES adjacent enemies between charges — a moving
 *  grinder with dashes as the burst layer. Loosed quickens bites too. */
export const HOUND_BITE_PERIOD = 0.35;
export const HOUND_BITE_PERIOD_LOOSED = 0.25; // ≈ ×0.72, the 1.8→1.3 dash ratio
export const HOUND_BITE_DAMAGE = 14; // 18 probed identical — adjacency-limited past here
/** Wide jaws: bites reach the crowd's edge from Bram's side — probed far
 *  better than sending him INTO the crowd (contact chip shredded him). */
export const HOUND_BITE_RANGE = 84;
/** Mourne floor-start corpse floor (probe: dense floors starved the
 *  opener, 76→17 k/min on floor 3) — never clamps a bigger carried reserve. */
export const MOURNE_FLOOR_RESERVE = 6;
export const EBB_HP_PER_SECOND = 0.09; // baseline trickle while the chain feeds
/** M0 rework: the drain only threatens when he ISN'T fighting. With no leech
 *  connection for EBB_QUIET_SECONDS the drain runs ×EBB_QUIET_MULT (~1 heart
 *  per 3.7s) — standing back is what kills him, the thick of the fight is
 *  safety. Makes "the quiet moments kill him" literally true (feel pass:
 *  the flat 0.09/s was decorative against a 0.75 HP/s drink). */
export const EBB_QUIET_SECONDS = 3;
export const EBB_QUIET_MULT = 3;
/** Cask burn rework (2026-07-27, Sarah: "I just end up dying later"):
 *  the burns stop being a countdown. RIM: only the blast CORE self-burns
 *  (edge-of-your-own-explosion spacing is the skill). FLOOR: burns never
 *  scorch max HP below this fraction. COOL: scorched HP mends 1 point per
 *  interval mid-floor (descent still clears everything at once). */
export const CASK_SAFE_RIM = 0.6; // self-hit only inside this × splash radius
export const CASK_BURN_FLOOR = 0.5; // max HP never scorches below this fraction
export const CASK_COOL_SECONDS = 25;

// Round-5 nerf (2026-07-28, Sarah: "just too OP, I don't have to play
// careful at all"): the drink stops outpacing incoming damage; the
// thick-of-the-fight identity survives, the immortality doesn't.
export const LEECH_DRINK = 0.14; // HP per enemy hit by Leech Chain
export const LEECH_DRINK_UPGRADED = 0.25;
/** Only the first N links drink (damage is uncapped) — dense floors must
 *  not scale his sustain (probe 2026-07-27: 0 deaths in 15 mortal runs;
 *  depth made him SAFER). The thick of the fight stays safety, not immortality. */
export const LEECH_DRINK_MAX_HITS = 3;

/** Apply run-start aspect state (called from World ctor via RunState). */
export function aspectHealBlock(rs: RunState): 'none' | 'all' | 'exceptLeech' {
  if (rs.character === 'vane') return 'all';
  if (rs.character === 'thirl') return 'exceptLeech';
  return 'none';
}

/**
 * Lethal-hit interception (World.damagePlayer). True = the death was bought
 * off; the funnel skips the hit entirely.
 */
export function aspectLethalSave(world: World, _incoming: number): boolean {
  const rs = world.runState;
  const pl = world.player;
  if (rs.character === 'vane' && rs.reviveReady) {
    // Ninefold: one death per floor is refused. Descent is her heal.
    rs.reviveReady = false;
    pl.hp = 1;
    pl.iframes = Math.round(NINEFOLD_IFRAME_SECONDS * SIM_HZ);
    world.events.emit('ninefoldSaved', {});
    return true;
  }
  return false;
}

/**
 * Ninefold (Vane, 2026-07-27): descent IS her heal — the one recovery her
 * all-blocking aspect allows. Direct set, not the (blocked) heal path.
 * Called from RunScene's real-descent path only — NEVER vault enter/exit.
 * True = she was mended (the scene toasts it).
 */
export function aspectDescendHeal(rs: RunState, player: Player): boolean {
  if (rs.character !== 'vane' || !player.alive || player.hp >= player.maxHp) return false;
  player.hp = player.maxHp;
  return true;
}

/**
 * Wick refuels by DOING things, not by existing: bounded, interact-gated
 * relights keep the descent pressure and the ride-it-empty Guttering
 * playstyle intact (you choose when to open the chest). Entrance-shop
 * purchases are deliberately NOT a source — the refuel can't be bought at
 * spawn. No-op for everyone else.
 */
export function relightCandle(rs: RunState, seconds = RELIGHT_EVENT_SECONDS): void {
  if (rs.character !== 'wick') return;
  rs.candle = Math.min(1, rs.candle + seconds / CANDLE_SECONDS);
}

/** Outgoing-damage aspect multipliers (folded into skillOutgoingDamageMult). */
export function aspectDamageMult(rs: RunState): number {
  if (rs.character === 'wick') {
    // Guttering: hottest right before he banks. Flare's fury is ADDITIVE on
    // top (multiplicative would double-dip the guttered ramp to ×2.4; this
    // caps the stack at ×2.1 and keeps slow-ramp vs purchased-spike distinct).
    return 1 + GUTTERING_MAX_BONUS * (1 - rs.candle)
      + (rs.flareLeft > 0 ? FLARE_BUFF_BONUS : 0);
  }
  return 1;
}

/** Corpse hook (Mourne): feed the reserve / sprout crawlers at the corpse. */
export function aspectOnKill(world: World, x: number, y: number): void {
  const rs = world.runState;
  const rotBloom = rs.abilities.find((a) => a.defId === 'rotBloom');
  if (!rotBloom) return;
  const p = skillParams(rotBloom);
  for (let i = 0; i < p.count; i++) {
    if (world.crawlers.length < crawlerCap(world.floor)) {
      spawnCrawler(world, x, y, p.damage, p.duration);
    } else if (rs.corpseReserve < CORPSE_RESERVE_CAP) {
      rs.corpseReserve++;
    }
  }
}

/**
 * Nova + optional knockback around a point. Hoisted from actives.ts so the
 * Sunder merge-nova (fired from tickAspects) can share it without a cycle.
 * `quiet` skips the generic burstFired — for callers with their own VFX event.
 */
export function slam(
  world: World, x: number, y: number, radius: number, damage: number,
  shove: number, quiet = false,
): void {
  for (let i = 0; i < world.enemies.length; i++) {
    const e = world.enemies[i]!;
    if (!e.alive) continue;
    if (dist2(x, y, e.x, e.y) > (radius + e.radius) ** 2) continue;
    world.damageEnemy(i, damage);
    if (shove > 0 && e.alive && e.type !== 'boss' && e.type !== 'keeper') {
      const d = Math.sqrt(dist2(x, y, e.x, e.y)) || 1;
      const nx = e.x + ((e.x - x) / d) * shove;
      const ny = e.y + ((e.y - y) / d) * shove;
      if (!world.tileMap.circleCollides(nx, ny, e.radius)) {
        e.x = nx;
        e.y = ny;
      }
    }
  }
  if (!quiet) world.events.emit('burstFired', { x, y, radius });
}

/** Shared with actives.ts (Mourne's Exhume). Callers own the cap check. */
export function spawnCrawler(world: World, x: number, y: number, damage: number, ttl: number): void {
  world.crawlers.push({
    x: x + gameplayRng.range(-10, 10),
    y: y + gameplayRng.range(-10, 10),
    damage,
    ttl,
    biteTick: 0,
  });
}

/** Floor arrival: Mourne re-materializes his reserve — he arrives armed. */
export function aspectOnFloorEnter(world: World): void {
  const rs = world.runState;
  const rotBloom = rs.abilities.find((a) => a.defId === 'rotBloom');
  if (rotBloom) {
    const p = skillParams(rotBloom);
    while (rs.corpseReserve > 0 && world.crawlers.length < crawlerCap(world.floor)) {
      rs.corpseReserve--;
      spawnCrawler(world, world.player.x, world.player.y, p.damage, p.duration);
    }
  }
}

/** Per-tick aspect simulation (World.update). */
export function tickAspects(world: World, dt: number): void {
  const rs = world.runState;
  const pl = world.player;

  // Wick — Guttering: the candle burns down; empty, it burns HIM. Holds its
  // breath while the boss is active (same pause as enrage): a protracted
  // duel shouldn't chip him — the ramp freezes wherever he took it in.
  if (rs.character === 'wick' && !world.vault && !(world.boss && world.boss.alive)) {
    rs.candle = Math.max(0, rs.candle - dt / CANDLE_SECONDS);
    if (rs.candle <= 0 && pl.alive) {
      world.candleChipAcc += dt;
      if (world.candleChipAcc >= GUTTERING_CHIP_PERIOD) {
        world.candleChipAcc = 0;
        world.chipPlayer(1);
        world.events.emit('candleEmpty', {});
      }
    }
  }
  // Flare's fury window burns down EVERYWHERE (vault, boss) — it's a 5s
  // purchased spike, not a resource; only the candle itself pauses above.
  if (rs.character === 'wick' && rs.flareLeft > 0) {
    rs.flareLeft = Math.max(0, rs.flareLeft - dt);
  }

  // Cask — powder burns COOL (2026-07-27): scorched max HP mends a point
  // every CASK_COOL_SECONDS mid-floor. syncPlayerStats credits the raised
  // ceiling next tick; descent still clears everything at once.
  if (rs.character === 'cask' && rs.burnedHp > 0 && pl.alive) {
    world.caskCoolAcc += dt;
    if (world.caskCoolAcc >= CASK_COOL_SECONDS) {
      world.caskCoolAcc = 0;
      rs.burnedHp -= 1;
    }
  }

  // Thirl — Ebb: HP decays continuously; the drain runs hot once the chain
  // has been quiet for a while (see EBB_QUIET_* comment). He cannot play
  // carefully, ever — now with teeth.
  if (rs.character === 'thirl' && pl.alive && !rs.god) {
    world.leechQuiet += dt;
    const mult = world.leechQuiet > EBB_QUIET_SECONDS ? EBB_QUIET_MULT : 1;
    pl.hp -= EBB_HP_PER_SECOND * mult * dt;
    if (pl.hp <= 0) {
      pl.hp = 0;
      pl.alive = false;
    }
  }

  // Barrow — Ironbound: taken damage converts to a damage aura.
  if (rs.character === 'barrow' && world.ironboundStacks > 0) {
    world.ironboundFade -= dt;
    if (world.ironboundFade <= 0) {
      world.ironboundStacks--;
      world.ironboundFade = IRONBOUND_STACK_SECONDS;
    }
    world.ironboundTick -= dt;
    if (world.ironboundTick <= 0) {
      world.ironboundTick = 0.5;
      const dps = world.ironboundStacks * IRONBOUND_DPS_PER_STACK;
      world.hash.query(pl.x, pl.y, IRONBOUND_AURA_RADIUS + 30, (idx) => {
        const e = world.enemies[idx];
        if (!e || !e.alive) return;
        if (dist2(pl.x, pl.y, e.x, e.y) <= (IRONBOUND_AURA_RADIUS + e.radius) ** 2) {
          world.damageEnemy(idx, dps * 0.5, 'dot');
        }
      });
    }
  }

  // Trine — Sunder: the window ticks here, but starting it is HER press
  // (actives.ts, MA) — the dial charges while sunderCd drains to 0 and then
  // waits. The old automatic cadence is gone: no press, no split.
  if (rs.character === 'trine') {
    if (world.sunderLeft > 0) {
      world.sunderLeft -= dt;
      pl.iframes = Math.max(pl.iframes, 2);
      if (world.sunderLeft <= 0) {
        // Merge bookend (round 5): she re-forms in a BURN detonation and
        // leaves scorched ground. splashBurst = the existing HOT ring.
        const areaMult = rs.stats.resolve('areaMult');
        const dmgMult = rs.stats.resolve('damageMult') * skillOutgoingDamageMult(rs);
        slam(world, pl.x, pl.y, SUNDER_NOVA_RADIUS * areaMult,
          SUNDER_NOVA_DAMAGE * dmgMult, 30, true);
        world.events.emit('splashBurst', {
          x: pl.x, y: pl.y, radius: SUNDER_NOVA_RADIUS * areaMult,
        });
        world.zones.push({
          x: pl.x, y: pl.y, radius: SUNDER_BURN_RADIUS * areaMult,
          duration: SUNDER_BURN_SECONDS, dps: SUNDER_BURN_DPS * dmgMult,
          slowPct: 0, kind: 'burn',
        });
        world.events.emit('sunderEnded', {});
      }
    } else if (world.sunderCd > 0) {
      world.sunderCd -= dt;
    }
  }

  tickCrawlers(world, dt);
  tickHound(world, dt);
}

/** Mourne's crawlers: melee-only, untargetable, live-position seek. */
function tickCrawlers(world: World, dt: number): void {
  // Exhume enrage: whipped fury decays here; read once for the whole pass.
  if (world.crawlerEnrage > 0) world.crawlerEnrage = Math.max(0, world.crawlerEnrage - dt);
  const enraged = world.crawlerEnrage > 0;
  const enrageSpeed = enraged ? CRAWLER_ENRAGE_SPEED : 1;
  // Ambient birth: with the swarm below the floor, one crawls out of Mourne
  // every AMBIENT_BIRTH_PERIOD — free (reserve untouched). Without this a
  // fresh Mourne has zero damage sources and can never make the first corpse.
  // Runs in the vault too: the swarm follows Mourne through the door, and
  // births keep the minimum alive inside (crawler TTL would otherwise wipe
  // the escort within seconds of entering).
  const rotBloom = world.runState.abilities.find((a) => a.defId === 'rotBloom');
  if (rotBloom) {
    // Boss duels: spend the reserve to hold a bigger swarm (see the constants'
    // comment). Free ambient births still cover the bootstrap minimum.
    const bossFeed = world.boss !== null && world.boss.alive
      && world.runState.corpseReserve > 0
      && world.crawlers.length >= AMBIENT_CRAWLER_FLOOR;
    const swarmFloor = bossFeed ? BOSS_SWARM_FLOOR : AMBIENT_CRAWLER_FLOOR;
    if (world.crawlers.length >= swarmFloor) {
      world.ambientBirthAcc = 0; // no instant refill the moment one dies
    } else {
      world.ambientBirthAcc += dt;
      if (world.ambientBirthAcc >= (bossFeed ? BOSS_BIRTH_PERIOD : AMBIENT_BIRTH_PERIOD)) {
        world.ambientBirthAcc = 0;
        if (bossFeed) world.runState.corpseReserve--;
        const p = skillParams(rotBloom);
        spawnCrawler(world, world.player.x, world.player.y, p.damage, p.duration);
      }
    }
  }
  for (let c = world.crawlers.length - 1; c >= 0; c--) {
    const cr = world.crawlers[c]!;
    cr.ttl -= dt;
    if (cr.ttl <= 0) {
      world.crawlers.splice(c, 1);
      continue;
    }
    if (cr.biteTick > 0) cr.biteTick -= dt;
    // Seek the nearest live enemy (live positions — reactive, unseeded).
    // Leashed: only prey near MOURNE counts — a crawler-relative search
    // chain-strays across the map, one 700px hop at a time.
    const pl0 = world.player;
    let target = -1;
    let bestD = 700 * 700;
    // Enraged: a SPECIAL inside the leash outranks nearer trash (the leash
    // itself never widens — that's the anti-stray contract).
    let specialTarget = -1;
    let specialD = 700 * 700;
    for (let i = 0; i < world.enemies.length; i++) {
      const e = world.enemies[i]!;
      if (!e.alive) continue;
      if (dist2(pl0.x, pl0.y, e.x, e.y) > CRAWLER_HUNT_RADIUS * CRAWLER_HUNT_RADIUS) continue;
      const d = dist2(cr.x, cr.y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        target = i;
      }
      if (enraged && isSpecialEnemy(e) && d < specialD) {
        specialD = d;
        specialTarget = i;
      }
    }
    if (specialTarget >= 0) {
      target = specialTarget;
      bestD = specialD;
    }
    if (target < 0) {
      // Idle: drift near the player.
      const pl = world.player;
      const d = Math.sqrt(dist2(cr.x, cr.y, pl.x, pl.y)) || 1;
      if (d > 90) {
        cr.x += ((pl.x - cr.x) / d) * CRAWLER_SPEED * 0.6 * dt;
        cr.y += ((pl.y - cr.y) / d) * CRAWLER_SPEED * 0.6 * dt;
      }
      continue;
    }
    const e = world.enemies[target]!;
    const d = Math.sqrt(bestD) || 1;
    if (d > e.radius + 8) {
      cr.x += ((e.x - cr.x) / d) * CRAWLER_SPEED * enrageSpeed * dt;
      cr.y += ((e.y - cr.y) / d) * CRAWLER_SPEED * enrageSpeed * dt;
    } else if (cr.biteTick <= 0) {
      cr.biteTick = CRAWLER_BITE_PERIOD;
      const bossClass = e.type === 'boss' || e.type === 'keeper';
      // Bites ride damageMult (M0): picks/artifacts finally reach the swarm,
      // and Mourne's own damage tax applies to his main engine too.
      const mult = world.runState.stats.resolve('damageMult')
        * (enraged ? CRAWLER_ENRAGE_DAMAGE : 1);
      world.damageEnemy(target, cr.damage * mult * (bossClass ? PET_BOSS_FACTOR : 1));
    }
  }
}

/** Bram & Gaunt's hound: Slipleash charges + Shared Fate vacuum/failure. */
function tickHound(world: World, dt: number): void {
  const hound = world.hound;
  if (!hound || !hound.alive) return;
  const rs = world.runState;
  const pl = world.player;
  const slipleash = rs.abilities.find((a) => a.defId === 'slipleash');
  const p = slipleash ? skillParams(slipleash) : null;

  // Contact chip: enemies grinding against the hound wear it down.
  hound.chipTick -= dt;
  if (hound.chipTick <= 0) {
    hound.chipTick = 0.5;
    world.hash.query(hound.x, hound.y, 40, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(hound.x, hound.y, e.x, e.y) <= (18 + e.radius) ** 2) hound.hp -= 1;
    });
    if (hound.hp <= 0) {
      hound.alive = false;
      // Shared Fate: everything unbanked on this floor dies with him.
      rs.gemsHeld = Math.max(0, rs.gemsHeld - rs.unbankedGems);
      rs.unbankedGems = 0;
      world.events.emit('houndDied', { x: hound.x, y: hound.y });
      return;
    }
  }

  switch (hound.state) {
    case 'follow': {
      // He HEELS — a harry-into-the-crowd variant probed WORSE (the contact
      // chip shredded him in seconds, then B&G had nothing). Wide jaws from
      // Bram's side are the grinder instead (HOUND_BITE_RANGE).
      const d = Math.sqrt(dist2(hound.x, hound.y, pl.x, pl.y)) || 1;
      if (d > 70) {
        hound.x += ((pl.x - hound.x) / d) * 300 * dt;
        hound.y += ((pl.y - hound.y) / d) * 300 * dt;
      }
      // Bites between charges (2026-07-27): whatever grinds against Gaunt
      // gets chewed — same recipe as crawler bites (damageMult + boss factor).
      if (p) {
        hound.biteTick -= dt;
        if (hound.biteTick <= 0) {
          hound.biteTick = slipleash?.upgraded ? HOUND_BITE_PERIOD_LOOSED : HOUND_BITE_PERIOD;
          const mult = rs.stats.resolve('damageMult');
          world.hash.query(hound.x, hound.y, HOUND_BITE_RANGE + 30, (idx) => {
            const e = world.enemies[idx];
            if (!e || !e.alive) return;
            if (dist2(hound.x, hound.y, e.x, e.y) <= (HOUND_BITE_RANGE + e.radius) ** 2) {
              const bossClass = e.type === 'boss' || e.type === 'keeper';
              world.damageEnemy(idx, HOUND_BITE_DAMAGE * mult * (bossClass ? PET_BOSS_FACTOR : 1));
            }
          });
        }
      }
      if (p) {
        hound.charge -= dt;
        if (hound.charge <= 0) {
          // Pick the nearest enemy and commit.
          let target: { x: number; y: number } | null = null;
          let bestD = 520 * 520;
          for (const e of world.enemies) {
            if (!e.alive) continue;
            const d2 = dist2(hound.x, hound.y, e.x, e.y);
            if (d2 < bestD) {
              bestD = d2;
              target = e;
            }
          }
          if (target) {
            const aim = Math.atan2(target.y - hound.y, target.x - hound.x);
            hound.dashVx = Math.cos(aim) * HOUND_DASH_SPEED;
            hound.dashVy = Math.sin(aim) * HOUND_DASH_SPEED;
            hound.state = 'dash';
            hound.stateLeft = 0.4;
            hound.dashHit.clear();
            world.events.emit('houndDashed', { x: hound.x, y: hound.y });
          }
          hound.charge = p.cooldown;
        }
      }
      break;
    }
    case 'dash': {
      hound.x += hound.dashVx * dt;
      hound.y += hound.dashVy * dt;
      hound.stateLeft -= dt;
      if (p) {
        world.hash.query(hound.x, hound.y, 50, (idx) => {
          const e = world.enemies[idx];
          if (!e || !e.alive || hound.dashHit.has(e.id)) return;
          if (dist2(hound.x, hound.y, e.x, e.y) <= (20 + e.radius) ** 2) {
            hound.dashHit.add(e.id);
            const bossClass = e.type === 'boss' || e.type === 'keeper';
            // Dashes ride damageMult (M0, same rule as crawler bites).
            const mult = world.runState.stats.resolve('damageMult');
            world.damageEnemy(idx, p.damage * mult * (bossClass ? PET_BOSS_FACTOR : 1));
          }
        });
      }
      if (hound.stateLeft <= 0) hound.state = 'follow';
      break;
    }
  }
}
